import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Search, Plus } from 'lucide-react';
import { fetchEstimatesList, fetchEstimateWorkspace, deleteEstimateDraft } from '../lib/bridge';
import { LoadingState } from '../components/ui/LoadingState';
import { ErrorState } from '../components/ui/ErrorState';
import { EmptyState } from '../components/ui/EmptyState';
import { PageHeader } from '../components/ui/PageHeader';
import { Card, CardHeader } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { fieldControlClass } from '../components/ui/forms';

// Stage 3 (docs/V6_ARCHITECTURE_AND_MIGRATION_PLAN.md §8): the estimates
// pipeline directory (reuses the same jobsList action as /jobs with
// filter="estimate" — no new backend logic for the list itself), plus
// entry points into the Full/Quick Estimate builder (/estimates/builder)
// and any of the signed-in employee's saved drafts.
export function EstimatesPage() {
  const [search, setSearch] = useState('');
  const query = useQuery({ queryKey: ['estimates', search], queryFn: () => fetchEstimatesList(search) });
  const queryClient = useQueryClient();
  const workspaceQuery = useQuery({ queryKey: ['estimateWorkspace'], queryFn: fetchEstimateWorkspace });
  const deleteDraftMutation = useMutation({
    mutationFn: (draftId: string) => deleteEstimateDraft(draftId),
    onSuccess: (result) => result.ok && void queryClient.invalidateQueries({ queryKey: ['estimateWorkspace'] }),
  });

  return (
    <div className="space-y-4">
      <PageHeader
        eyebrow="Sales"
        title="Estimates"
        actions={
          <>
            <Link to="/estimates/price-book" className="text-sm font-semibold text-gold-300 hover:text-gold-200">
              Price Book
            </Link>
            <Link to="/estimates/builder">
              <Button>
                <Plus className="h-4 w-4" aria-hidden="true" />
                New Estimate
              </Button>
            </Link>
          </>
        }
      />

      {workspaceQuery.data?.ok && (workspaceQuery.data.drafts?.length ?? 0) > 0 && (
        <Card>
          <CardHeader title="My Drafts" />
          <ul className="space-y-2">
            {workspaceQuery.data.drafts!.map((d) => (
              <li key={d.draftId} className="flex items-center justify-between gap-3 rounded-xl border border-ink-border bg-ink p-3">
                <Link to={`/estimates/builder?draftId=${encodeURIComponent(d.draftId)}`} className="min-w-0 flex-1">
                  <p className="truncate font-semibold text-white">{d.customerName || 'Untitled'}</p>
                  <p className="truncate text-sm text-slate-400">{d.project}</p>
                </Link>
                <button
                  type="button"
                  disabled={deleteDraftMutation.isPending}
                  onClick={() => deleteDraftMutation.mutate(d.draftId)}
                  className="shrink-0 text-xs font-semibold text-red-300 hover:text-red-200 disabled:opacity-60"
                >
                  Delete
                </button>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <div className="relative max-w-sm">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" aria-hidden="true" />
        <input className={`${fieldControlClass} pl-9`} placeholder="Search estimates…" value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>

      {query.isLoading && <LoadingState label="Loading estimates…" />}
      {query.isError && (
        <ErrorState message={query.error instanceof Error ? query.error.message : 'Could not load estimates.'} onRetry={() => query.refetch()} />
      )}
      {query.data && !query.data.ok && <ErrorState message={query.data.message || 'Could not load estimates.'} onRetry={() => query.refetch()} />}
      {query.data?.ok && (query.data.jobs?.length ?? 0) === 0 && <EmptyState>No estimates found.</EmptyState>}
      {query.data?.ok && (query.data.jobs?.length ?? 0) > 0 && (
        <ul className="space-y-2">
          {query.data.jobs!.map((job) => (
            <li key={job.jobId}>
              <Link
                to={`/estimates/${encodeURIComponent(job.jobId)}`}
                className="flex items-center justify-between gap-3 rounded-xl border border-ink-border bg-ink-surface p-4 shadow-card transition hover:border-gold-700/60 hover:bg-ink-raised"
              >
                <div className="min-w-0">
                  <p className="truncate font-semibold text-white">{job.customer || job.jobId}</p>
                  <p className="truncate text-sm text-slate-400">{job.project || job.documentNo}</p>
                </div>
                <Badge tone="gold" className="shrink-0">
                  {job.estimateStatus || job.stage || '—'}
                </Badge>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
